import { pageTitle } from '@palaccount/config';
import type { StockCountResponse } from '@palaccount/contracts';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { Ltr, Qty } from '@/components/amount';
import { ConfirmBox } from '@/components/confirm-box';
import { DocumentToolbar } from '@/components/document-toolbar';
import { ReasonBox } from '@/components/reason-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { denyPermission, useCan } from '@/lib/permissions';
import { formatQty } from '@/lib/format';
import { ApiError, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import {
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from '@/features/sales/tracking-capture';
import { TrackingRows } from '@/features/sales/tracking-rows';

import { trimZeros } from './conversion';
import { CountStatusChip } from './stock-chips';
import { useStockCount, useStockCountMutations, useWarehouses } from './stock-api';
import { useOrgSetting } from '@/features/admin/api';

import { computeVariance, VARIANCE_CLASSES, varianceSeverity } from './stock-format';

const COUNTED_RE = /^\d{1,12}(\.\d{1,3})?$/;

/**
 * A count row read as the capture component reads a document line. A count
 * line is one item and never a kit that expands — the snapshot is taken from
 * `stock_levels`, and an auto-deducting kit is never stocked — so the block
 * it produces is always the row's own.
 */
function asTrackedItem(line: StockCountResponse['lines'][number]): TrackedItem {
  return {
    id: line.itemId,
    name: line.itemName,
    type: 'product',
    autoDeduct: false,
    trackingType: line.trackingType,
    components: [],
  };
}

/**
 * INV-05 §5.7 — the stock take. An OPEN count is a scratchpad: counts typed,
 * retyped and cleared, and NOTHING moves (BR-INV-05-18). A blank counted
 * quantity means "not counted" and is never coerced to zero (BR-INV-05-13).
 *
 * The sign rule is on screen, labelled, because it is the one thing everybody
 * misreads: variance = system − counted, and POSITIVE means MISSING
 * (BR-INV-05-12).
 *
 * WHICH UNITS ARE ON THE SHELF. §5.7 draws four columns and no identifier,
 * but BR-INV-05-15 makes the count POST a movement, and a Stock Take of a
 * serial-tracked item that names no serial puts an anonymous unit on the
 * shelf — the thing BR-MD-03-17 exists to prevent. So the counted quantity
 * stays field 29's own number and the same sub-row the sales grids use sits
 * under it, saying which units make that number up. Typing 3 asks for 3
 * serials and says how many are still to scan; the server resolves the
 * difference per unit.
 */
export function CountEditorPage() {
  const { t } = useTranslation();
  /*
   * INV-05 §5.7's amber band — the company's own tolerance (OQ-16-05).
   *
   * BR-INV-05-17: "the variance severity threshold must be a configurable
   * tolerance, not the hard-coded 10". THIS READ is the rule — an org setting
   * an accountant can move, resolved per tenant, not a literal in a formatter.
   * The 10 survives only as `SETTINGS_REGISTRY`'s default, which is where a
   * default belongs. Passing this into `varianceSeverity` below is what makes
   * the colour follow the company rather than the code.
   */
  const countTolerance = useOrgSetting<number>('inventory.count_variance_tolerance');
  const navigate = useNavigate();
  const { countId } = useParams<{ countId: string }>();
  const isNew = !countId;

  const { data: count, isPending: loading } = useStockCount(countId);
  const { data: warehouses } = useWarehouses({ status: 'active' });
  const mutations = useStockCountMutations(countId ?? '');

  const canEdit = useCan('inventory.edit');

  const [warehouseId, setWarehouseId] = useState('');
  const [counted, setCounted] = useState<Record<string, string>>({});
  /** Per item, the capture text the sub-row holds (one block: the item). */
  const [tracking, setTracking] = useState<Record<string, CaptureText>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * THE POST ASKS THROUGH THE PRODUCT'S OWN BOX NOW — BR-PERM-15 (spec line
   * 35168): "Every irreversible action must use one of these two patterns. A
   * new pattern must not be invented." Posting a count IS irreversible,
   * established in the product rather than assumed: `stockCountsContract`
   * declares no unpost route, a posted count answers 409 `notOpen` to every
   * further write (stock-counts.service.ts:240), and the product's own
   * `counts.postedNotice` says the correction is a NEW count — a new act, not
   * an undo. So the body carries GEN-205's undone clause, byte-identical to
   * `common.confirmDeleteUndone`, and `count-post-asks.test.tsx` asserts it
   * PRESENT — the mirror of the absences the reversible acts pin.
   *
   * The old ask was `window.confirm`, which is NEITHER of §16.5.3's two
   * patterns — browser chrome with an OK button, no padlock, no amber tile,
   * no translation (the argument confirm-box.tsx's header makes in full).
   *
   * GEN-209 IS DELIBERATELY NOT CITED. Its seven acts include "posting a
   * revaluation", and a stock take's write-off/found entry revalues stock —
   * but reading "count post" into that phrase is an inference, and BR-PERM-15
   * reaches this site without it: the act is irreversible, so it takes the
   * shared box on that ground alone. (confirm-box.tsx's header paraphrases
   * GEN-209's roster as including count posts; if that reading is ever
   * settled, the citation belongs here — the behaviour does not change.)
   *
   * ── WHY THE BODY NAMES NO FIGURE (GEN-210 CONSIDERED AND NOT CITED) ──────
   *
   * GEN-210 wants an amount or count named where money or stock data dies.
   * The figure this screen holds is the variance against the SNAPSHOT; the
   * post recomputes every delta against the LIVE balance under lock
   * (stock-counts.service.ts, INV-05 §15's two-users case), so the number on
   * screen can be false at the moment of the write. The body therefore states
   * the mechanism — one movement per line that differs — which is true under
   * every race, rather than a figure that is true under none of them.
   *
   * The CANCEL beside this button asks through §8's OTHER pattern — the
   * typed-reason `ReasonBox` — because GEN-59 (spec line 3806) says "Cancel
   * must always ask for a reason, and must store that reason on the record".
   * The contract now carries it (stock-counts.ts cancel body: `reason`
   * required, 1..500), the service writes `cancel_reason` on the row and the
   * reason onto the audit row (20260903100000), and the cancelled notice
   * below reads it back. The undone clause is in the prompt because the act
   * is terminal, established not assumed: no un-cancel route on the
   * contract, and a cancelled count answers 409 `notOpen` to every write —
   * correcting means a NEW count, a new act, not an undo.
   */
  const [postAsk, setPostAsk] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t(isNew ? 'counts.newTitle' : 'counts.editTitle'));
  }, [t, isNew]);

  useEffect(() => {
    if (!count) return;
    const stamp = `${count.id}:${count.updatedAt}:${count.status}`;
    if (loaded.current === stamp) return;
    loaded.current = stamp;
    const next: Record<string, string> = {};
    const captured: Record<string, CaptureText> = {};
    for (const line of count.lines) {
      next[line.itemId] = line.countedQty === null ? '' : trimZeros(line.countedQty);
      captured[line.itemId] = textFromStored({
        serialNos: line.serialNos,
        batchNo: line.batchNo,
      });
    }
    setCounted(next);
    setTracking(captured);
    setError(null);
  }, [count]);

  const handleFailure = (thrown: unknown, permission: 'create' | 'edit' | 'approve') => {
    if (thrown instanceof ApiError && thrown.status === 403) {
      denyPermission(`inventory.${permission}`);
    }
    // D46: the cause rides along — the count's post door raises the serial
    // presence/uniqueness refusals AND the warehouse wall (wh.errClosed at
    // the count snapshot), all structured, key+params for the Arabic reader.
    setError(
      localizeServerMessage(t, thrown instanceof Error ? thrown.message : String(thrown), thrown),
    );
  };

  const onCreate = async () => {
    if (!warehouseId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await mutations.create.mutateAsync({ warehouseId });
      void navigate(`/inventory/counts/${created.id}`, { replace: true });
    } catch (thrown) {
      handleFailure(thrown, 'create');
    } finally {
      setBusy(false);
    }
  };

  const onSave = async () => {
    if (!count) return;
    for (const value of Object.values(counted)) {
      if (value.trim() !== '' && !COUNTED_RE.test(value.trim())) {
        setError(t('counts.errQty'));
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      // A replace-set: a blank box clears the entry back to "not counted",
      // and with it the units it claimed to have seen — an identifier that
      // outlived its count would be a statement nobody made.
      await mutations.update.mutateAsync({
        counts: count.lines.map((line) => {
          const typed = (counted[line.itemId] ?? '').trim();
          const payload = trackingPayload(
            captureBlocks(asTrackedItem(line), typed),
            tracking[line.itemId] ?? {},
          );
          return {
            itemId: line.itemId,
            countedQty: typed === '' ? null : typed,
            serialNos: payload.serialNos ?? null,
            batchNo: payload.batchNo ?? null,
          };
        }),
      });
      loaded.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'edit');
    } finally {
      setBusy(false);
    }
  };

  const run = async (action: () => Promise<unknown>, permission: 'edit' | 'approve') => {
    setBusy(true);
    setError(null);
    try {
      await action();
      loaded.current = null;
    } catch (thrown) {
      handleFailure(thrown, permission);
    } finally {
      setBusy(false);
    }
  };

  // ── /new: pick a location, and the count snapshots what it holds ────────
  if (isNew) {
    return (
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="me-auto text-lg font-bold">{t('counts.newTitle')}</h2>
          <Button asChild variant="outline">
            <Link to="/inventory/counts">{t('common.cancel')}</Link>
          </Button>
        </div>
        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            {error}
          </p>
        ) : null}
        <Card>
          <CardContent className="grid max-w-md gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="ct-warehouse">{t('counts.fWarehouse')}</Label>
              <NativeSelect
                id="ct-warehouse"
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
              >
                <option value="">{t('counts.pickWarehouse')}</option>
                {(warehouses ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <Gated
              permission="inventory.create"
              reasonKey="perm.cannot.createCounts"
              className="justify-self-start"
            >
              <Button
                className="justify-self-start"
                disabled={!warehouseId || busy}
                onClick={() => void onCreate()}
              >
                {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
                {t('counts.startCount')}
              </Button>
            </Gated>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  if (!count) {
    return <p className="py-12 text-center text-sm text-destructive">{t('common.loadFailed')}</p>;
  }

  const isOpen = count.status === 'open';
  const editable = isOpen && canEdit;

  return (
    <div className="grid gap-4">
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('counts.editTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          <Ltr value={count.code} />
        </span>
        <span className="text-sm">{count.warehouseName}</span>
        <Ltr value={count.countDate} className="font-mono text-sm text-muted-foreground" />
        <CountStatusChip status={count.status} />
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          actions={[
            {
              // Not PART 4.6's Cancel row: a link back to the list wearing the
              // same word. The primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/inventory/counts',
            },
            {
              // GEN-208: the click OPENS the question and does nothing else.
              // The write happens on the dialog's own verb, with GEN-59's
              // typed reason, never here.
              id: 'cancel-count',
              row: 'cancel',
              label: t('counts.cancelCount'),
              variant: 'outline',
              visible: isOpen,
              gate: { permission: 'inventory.edit', reasonKey: 'perm.cannot.editCounts' },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              variant: 'outline',
              visible: isOpen,
              gate: { permission: 'inventory.edit', reasonKey: 'perm.cannot.editCounts' },
              busy,
              onAct: () => void onSave(),
            },
            {
              // GEN-208, spec line 4707: the click OPENS the question and
              // does nothing else. The write happens on the dialog's own
              // verb, never here.
              id: 'post',
              row: 'confirm',
              label: t('counts.post'),
              visible: isOpen,
              gate: { permission: 'inventory.approve', reasonKey: 'perm.cannot.postCounts' },
              disabled: busy,
              onAct: () => setPostAsk(true),
            },
          ]}
        >
          {isOpen ? (
            <>
              {/* Inside the same `isOpen` guard as the button, so the dialog
                  cannot outlive the state that justifies it. `gen72.confirmCancel`
                  is the verb — deliberately NOT the trigger's exact words
                  (reason-box.tsx's own rule), and already distinct from the
                  dialog's Cancel button. */}
              <ReasonBox
                open={cancelAsk}
                title={t('counts.confirmCancelTitle')}
                prompt={t('counts.confirmCancelBody')}
                reasonLabel={t('gen72.cancelReasonLabel')}
                confirmLabel={t('gen72.confirmCancel')}
                busy={busy}
                onConfirm={(reason) => {
                  setCancelAsk(false);
                  void run(() => mutations.cancel.mutateAsync(reason), 'edit');
                }}
                onCancel={() => setCancelAsk(false)}
              />
              {/* Inside the same `isOpen` guard as the button, so the dialog
                  cannot outlive the state that justifies it: a count that
                  leaves `open` under a standing question has nothing left to
                  post — the server would answer 409 `notOpen`.

                  `counts.post` ("Post count") is reused as the action label
                  rather than a new key: GEN-206 wants the VERB, this already
                  is one, and it is the same words as the button that raised
                  the dialog — the property §8 is after when it says the
                  button repeats the verb. */}
              <ConfirmBox
                open={postAsk}
                title={t('counts.confirmPostTitle')}
                body={t('counts.confirmPostBody')}
                confirmLabel={t('counts.post')}
                busy={busy}
                onConfirm={() => {
                  setPostAsk(false);
                  void run(() => mutations.post.mutateAsync(), 'approve');
                }}
                onCancel={() => setPostAsk(false)}
              />
            </>
          ) : null}
        </DocumentToolbar>
      </div>

      {count.status === 'posted' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('counts.postedNotice')}
        </p>
      ) : null}
      {count.status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {/* GEN-59's stored reason, read back OFF THE RECORD. Rows cancelled
              before the reason existed keep the old sentence — interpolating
              null as '' would render "cancelled: ." and look like data loss. */}
          {count.cancelReason !== null
            ? t('counts.cancelledReasonNotice', { reason: count.cancelReason })
            : t('counts.cancelledNotice')}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* BR-INV-05-12 — the sign rule, spelled out where the numbers are. */}
      <p className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
        {t('counts.varianceLabel')}
      </p>

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('counts.colItem')}</TableHead>
                <TableHead className="text-end">{t('counts.colSystemQty')}</TableHead>
                <TableHead className="w-32 text-end">{t('counts.colCountedQty')}</TableHead>
                <TableHead className="w-28 text-end">{t('counts.colVariance')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {count.lines.map((line, index) => {
                const typed = counted[line.itemId] ?? '';
                const variance = computeVariance(line.systemQty, typed);
                const severity = varianceSeverity(variance, countTolerance);
                const blocks = captureBlocks(asTrackedItem(line), typed);
                return (
                  <React.Fragment key={line.id}>
                  <TableRow className="odd:bg-muted/30">
                    <TableCell>
                      <span className="grid">
                        <span className="text-sm font-medium">{line.itemName}</span>
                        <Ltr
                          value={line.itemCode}
                          className="font-mono text-xs text-muted-foreground"
                        />
                      </span>
                    </TableCell>
                    <TableCell className="text-end">
                      {/* `N-4` — the book figure is a DISPLAY, so it carries the
                          decimals its unit allows and its unit, composed into
                          one island. It used to be `trimZeros` beside a loose
                          symbol: "200" for a kilogram shelf, and a number and a
                          symbol the RTL layout was free to reorder. */}
                      <Qty
                        value={line.systemQty}
                        precision={line.unitPrecision}
                        unit={line.unitSymbol}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        aria-label={t('counts.cellCounted', { item: line.itemName })}
                        dir="ltr"
                        inputMode="decimal"
                        placeholder={t('counts.notCounted')}
                        className="h-9 text-end font-mono"
                        value={typed}
                        disabled={!editable}
                        onChange={(event) =>
                          setCounted((current) => ({
                            ...current,
                            [line.itemId]: event.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell
                      className={cn('text-end font-mono font-semibold', VARIANCE_CLASSES[severity])}
                    >
                      {/*
                        * `N-4`'s DECIMALS, and deliberately not a second
                        * symbol. The variance is `BR-INV-05-12`'s subtraction
                        * of the SAME measurement on the SAME row — system
                        * minus counted — and the only other DISPLAY of that
                        * measurement, the system figure, already prints the
                        * unit. The cell between them is a text box, which is
                        * not a display of anything. So this is INV-06's
                        * running-balance call, not a new one: printing "pcs"
                        * twice in one row for one quantity and its difference
                        * is the noise the rule does not ask for.
                        */}
                      {variance === null ? (
                        '—'
                      ) : (
                        <Ltr
                          value={formatQty(variance, line.unitPrecision)}
                          className="font-mono tabular-nums font-semibold"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  {/* The units behind the number, in the same sub-row the
                      sales grids use — one interaction for one rule. */}
                  <TrackingRows
                    blocks={blocks}
                    text={tracking[line.itemId] ?? {}}
                    onChange={(blockKey, value) =>
                      setTracking((current) => ({
                        ...current,
                        [line.itemId]: { ...(current[line.itemId] ?? {}), [blockKey]: value },
                      }))
                    }
                    disabled={!editable}
                    columns={4}
                    line={index + 1}
                  />
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
